import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, InputLabel, Stack, TextField, Select, MenuItem, InputAdornment, IconButton, Alert, Button, Typography, Slider } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RotateRightRoundedIcon from "@mui/icons-material/RotateRightRounded";
import { FlipHorizontal, FlipVertical } from "@zesty-io/material";
import BlurOnRoundedIcon from "@mui/icons-material/BlurOnRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { isEmpty, omitBy } from "lodash";
interface Props {
  url: string;
  setShowEdit: (show: boolean) => void;
  imageSettings: any;
  setImageSettings: (settings: any) => void;
}
export const OTFEditor = ({
  url,
  setShowEdit,
  imageSettings,
  setImageSettings
}: Props) => {
  const {
    t
  } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const newUrl = `${url}?${new URLSearchParams(omitBy(imageSettings, isEmpty)).toString()}`;
  const handleCopyClick = (data: string) => {
    navigator?.clipboard?.writeText(data).then(() => {
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 1500);
    }).catch(err => {
      console.error(err);
    });
  };
  const handleDone = () => {
    setShowEdit(false);
    setImageSettings(null);
  };
  useEffect(() => setImageSettings({
    ...imageSettings,
    fit: "cover"
  }), []);
  const handleRotate = () => {
    if (!imageSettings.orient) {
      // TODO: Refactor and find patterns
      setImageSettings({
        ...imageSettings,
        orient: "r"
      });
    } else if (imageSettings.orient === "r") {
      setImageSettings({
        ...imageSettings,
        orient: "vh"
      });
    } else if (imageSettings.orient === "vh") {
      setImageSettings({
        ...imageSettings,
        orient: "l"
      });
    } else if (imageSettings.orient === "l") {
      setImageSettings({
        ...imageSettings,
        orient: ""
      });
    } else if (imageSettings.orient === "h") {
      setImageSettings({
        ...imageSettings,
        orient: "lv"
      });
    } else if (imageSettings.orient === "lv") {
      setImageSettings({
        ...imageSettings,
        orient: "v"
      });
    } else if (imageSettings.orient === "v") {
      setImageSettings({
        ...imageSettings,
        orient: "rv"
      });
    } else if (imageSettings.orient === "rv") {
      setImageSettings({
        ...imageSettings,
        orient: "h"
      });
    }
  };
  return <Box data-sentry-element="Box" data-sentry-component="OTFEditor" data-sentry-source-file="OTFEditor.tsx">
      <Box sx={{
      p: 2,
      borderBottom: theme => `1px solid ${theme.palette.border}`
    }} data-sentry-element="Box" data-sentry-source-file="OTFEditor.tsx">
        <Stack direction="row" justifyContent="space-between" alignItems="center" data-sentry-element="Stack" data-sentry-source-file="OTFEditor.tsx">
          <Stack direction="row" alignItems="center" gap={1} data-sentry-element="Stack" data-sentry-source-file="OTFEditor.tsx">
            <IconButton size="small" sx={{
            height: "fit-content"
          }} onClick={handleDone} data-sentry-element="IconButton" data-sentry-source-file="OTFEditor.tsx">
              <ArrowBackRoundedIcon fontSize="small" color="action" data-sentry-element="ArrowBackRoundedIcon" data-sentry-source-file="OTFEditor.tsx" />
            </IconButton>
            <Typography variant="body1" data-sentry-element="Typography" data-sentry-source-file="OTFEditor.tsx">{t("media.otfEditorTitle")}</Typography>
          </Stack>
          <Button size="small" variant="contained" onClick={handleDone} data-sentry-element="Button" data-sentry-source-file="OTFEditor.tsx">
            {t("common.done")}
          </Button>
        </Stack>
      </Box>
      <Stack gap={2} sx={{
      p: 2
    }} data-sentry-element="Stack" data-sentry-source-file="OTFEditor.tsx">
        <Box data-sentry-element="Box" data-sentry-source-file="OTFEditor.tsx">
          <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="OTFEditor.tsx">{t("media.otfEditorFitLabel")}</InputLabel>
          <Select fullWidth value={imageSettings?.fit || ""} onChange={evt => setImageSettings({
          ...imageSettings,
          fit: evt.target.value
        })} displayEmpty data-sentry-element="Select" data-sentry-source-file="OTFEditor.tsx">
            <MenuItem value="" data-sentry-element="MenuItem" data-sentry-source-file="OTFEditor.tsx">{t("media.otfEditorFitNone")}</MenuItem>
            <MenuItem value="bounds" data-sentry-element="MenuItem" data-sentry-source-file="OTFEditor.tsx">{t("media.otfEditorFitBounds")}</MenuItem>
            <MenuItem value="cover" data-sentry-element="MenuItem" data-sentry-source-file="OTFEditor.tsx">{t("media.otfEditorFitCover")}</MenuItem>
            <MenuItem value="crop" data-sentry-element="MenuItem" data-sentry-source-file="OTFEditor.tsx">{t("media.otfEditorFitCrop")}</MenuItem>
          </Select>
        </Box>
        <Stack direction="row" gap={2} data-sentry-element="Stack" data-sentry-source-file="OTFEditor.tsx">
          <Box width="100%" data-sentry-element="Box" data-sentry-source-file="OTFEditor.tsx">
            <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="OTFEditor.tsx">{t("media.otfEditorWidthLabel")}</InputLabel>
            <TextField fullWidth placeholder={t("media.otfEditorAutoPlaceholder")} value={imageSettings?.width || ""} onChange={evt => setImageSettings({
            ...imageSettings,
            width: evt.target.value
          })} data-sentry-element="TextField" data-sentry-source-file="OTFEditor.tsx" />
          </Box>
          <Box width="100%" data-sentry-element="Box" data-sentry-source-file="OTFEditor.tsx">
            <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="OTFEditor.tsx">{t("media.otfEditorHeightLabel")}</InputLabel>
            <TextField fullWidth placeholder={t("media.otfEditorAutoPlaceholder")} value={imageSettings?.height || ""} onChange={evt => setImageSettings({
            ...imageSettings,
            height: evt.target.value
          })} data-sentry-element="TextField" data-sentry-source-file="OTFEditor.tsx" />
          </Box>
        </Stack>
        <Stack direction="row" gap={2} data-sentry-element="Stack" data-sentry-source-file="OTFEditor.tsx">
          <Box sx={{
          maxWidth: "186px"
        }} data-sentry-element="Box" data-sentry-source-file="OTFEditor.tsx">
            <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="OTFEditor.tsx">{t("media.otfEditorBlurLabel")}</InputLabel>
            <TextField fullWidth placeholder="0" value={imageSettings?.blur || ""} onChange={evt => {
            if (evt.target.value === "0") return;
            setImageSettings({
              ...imageSettings,
              blur: evt.target.value
            });
          }} InputProps={{
            startAdornment: <InputAdornment position="start">
                    <BlurOnRoundedIcon />
                  </InputAdornment>
          }} data-sentry-element="TextField" data-sentry-source-file="OTFEditor.tsx" />
          </Box>
          <Stack direction="row" gap={2} alignItems="flex-end" data-sentry-element="Stack" data-sentry-source-file="OTFEditor.tsx">
            <Button sx={{
            minWidth: "40px",
            width: "40px",
            height: "40px"
          }} color="inherit" variant="outlined" onClick={handleRotate} data-sentry-element="Button" data-sentry-source-file="OTFEditor.tsx">
              <RotateRightRoundedIcon color="action" data-sentry-element="RotateRightRoundedIcon" data-sentry-source-file="OTFEditor.tsx" />
            </Button>
            <Button color="inherit" variant="outlined" sx={{
            minWidth: "40px",
            width: "40px",
            height: "40px"
          }} onClick={() => {
            // TODO: Refactor and find patterns
            if (!imageSettings.orient) {
              setImageSettings({
                ...imageSettings,
                orient: "h"
              });
            } else if (imageSettings.orient === "h") {
              setImageSettings({
                ...imageSettings,
                orient: ""
              });
            } else if (imageSettings.orient === "vh") {
              setImageSettings({
                ...imageSettings,
                orient: "v"
              });
            } else if (imageSettings.orient === "v") {
              setImageSettings({
                ...imageSettings,
                orient: "vh"
              });
            } else if (imageSettings.orient === "r") {
              setImageSettings({
                ...imageSettings,
                orient: "rv"
              });
            } else if (imageSettings.orient === "l") {
              setImageSettings({
                ...imageSettings,
                orient: "lv"
              });
            } else if (imageSettings.orient === "rv") {
              setImageSettings({
                ...imageSettings,
                orient: "r"
              });
            } else if (imageSettings.orient === "lv") {
              setImageSettings({
                ...imageSettings,
                orient: "l"
              });
            }
          }} data-sentry-element="Button" data-sentry-source-file="OTFEditor.tsx">
              <FlipHorizontal color="action" data-sentry-element="FlipHorizontal" data-sentry-source-file="OTFEditor.tsx" />
            </Button>
            <Button color="inherit" variant="outlined" sx={{
            minWidth: "40px",
            width: "40px",
            height: "40px"
          }} onClick={() => {
            // TODO: Refactor and find patterns
            if (!imageSettings.orient) {
              setImageSettings({
                ...imageSettings,
                orient: "v"
              });
            } else if (imageSettings.orient === "v") {
              setImageSettings({
                ...imageSettings,
                orient: ""
              });
            } else if (imageSettings.orient === "vh") {
              setImageSettings({
                ...imageSettings,
                orient: "h"
              });
            } else if (imageSettings.orient === "h") {
              setImageSettings({
                ...imageSettings,
                orient: "vh"
              });
            } else if (imageSettings.orient === "r") {
              setImageSettings({
                ...imageSettings,
                orient: "lv"
              });
            } else if (imageSettings.orient === "l") {
              setImageSettings({
                ...imageSettings,
                orient: "rv"
              });
            } else if (imageSettings.orient === "rv") {
              setImageSettings({
                ...imageSettings,
                orient: "l"
              });
            } else if (imageSettings.orient === "lv") {
              setImageSettings({
                ...imageSettings,
                orient: "r"
              });
            }
          }} data-sentry-element="Button" data-sentry-source-file="OTFEditor.tsx">
              <FlipVertical color="action" data-sentry-element="FlipVertical" data-sentry-source-file="OTFEditor.tsx" />
            </Button>
          </Stack>
        </Stack>
        <Box data-sentry-element="Box" data-sentry-source-file="OTFEditor.tsx">
          <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="OTFEditor.tsx">{t("media.otfEditorSaturationLabel")}</InputLabel>
          <Slider aria-label={t("media.otfEditorSaturationAria")} valueLabelDisplay="auto" value={Number(imageSettings?.saturation) || 0} min={-100} max={100} onChange={(_, val) => setImageSettings({
          ...imageSettings,
          saturation: String(val)
        })} size="small" data-sentry-element="Slider" data-sentry-source-file="OTFEditor.tsx" />
        </Box>
        <Box data-sentry-element="Box" data-sentry-source-file="OTFEditor.tsx">
          <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="OTFEditor.tsx">{t("media.otfEditorOptimizeLabel")}</InputLabel>
          <Select fullWidth value={imageSettings?.optimize || ""} onChange={evt => setImageSettings({
          ...imageSettings,
          optimize: evt.target.value
        })} displayEmpty data-sentry-element="Select" data-sentry-source-file="OTFEditor.tsx">
            <MenuItem value="" data-sentry-element="MenuItem" data-sentry-source-file="OTFEditor.tsx">{t("media.otfEditorFitNone")}</MenuItem>
            <MenuItem value="high" data-sentry-element="MenuItem" data-sentry-source-file="OTFEditor.tsx">{t("media.otfEditorOptimizeHigh")}</MenuItem>
            <MenuItem value="medium" data-sentry-element="MenuItem" data-sentry-source-file="OTFEditor.tsx">
              {t("media.otfEditorOptimizeMedium")}
            </MenuItem>
            <MenuItem value="low" data-sentry-element="MenuItem" data-sentry-source-file="OTFEditor.tsx">{t("media.otfEditorOptimizeLow")}</MenuItem>
          </Select>
        </Box>
      </Stack>
      <Box sx={{
      position: "absolute",
      bottom: 0,
      p: 2,
      backgroundColor: "grey.900",
      borderTop: theme => `1px solid ${theme.palette.border}`
    }} data-sentry-element="Box" data-sentry-source-file="OTFEditor.tsx">
        <InputLabel sx={{
        textAlign: "center",
        color: "white"
      }} data-sentry-element="InputLabel" data-sentry-source-file="OTFEditor.tsx">
          {t("media.otfEditorCopyUrlLabel")}
        </InputLabel>
        <TextField fullWidth placeholder={t("media.otfEditorAutoPlaceholder")} value={newUrl} disabled InputProps={{
        readOnly: true,
        sx: {
          background: "white"
        },
        endAdornment: <InputAdornment position="end">
                {isCopied ? <CheckIcon /> : <IconButton onClick={() => handleCopyClick(newUrl)}>
                    <ContentCopyIcon />
                  </IconButton>}
              </InputAdornment>
      }} inputProps={{
        style: {
          WebkitTextFillColor: "unset"
        }
      }} data-sentry-element="TextField" data-sentry-source-file="OTFEditor.tsx" />
        <Alert severity="info" sx={{
        mt: 2
      }} data-sentry-element="Alert" data-sentry-source-file="OTFEditor.tsx">
          {t("media.otfEditorUnsavedWarning")}
        </Alert>
      </Box>
    </Box>;
};